// ===== BEGIN FILE: src/components/AdvancedNorthIndianChart.tsx =====
/**
 * Advanced North Indian Chart with beautiful backgrounds, interactive features,
 * and export capabilities for professional reports and presentations.
 * 
 * Features:
 * - Beautiful image backgrounds with SVG overlay
 * - Click on planets: Show detailed info (Nakshatra, dignity, combustion, retro, blended %)
 * - Hover on planets: Show aspect arrows to respective houses
 * - Export as PNG/JPEG for PowerPoint and reports
 * - Responsive design for web and mobile
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Card, Sheet, Typography, IconButton, Tooltip } from '@mui/joy';
import DownloadIcon from '@mui/icons-material/Download';
import PaletteIcon from '@mui/icons-material/Palette';
import ImageIcon from '@mui/icons-material/Image';
import { classifyFunctionalForLagna, RASHI_NAMES } from '../utils/astroEngine';
import '../styles/gradientBorder.css';

// ===================== TYPES =====================
type PlanetName = 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn' | 'Rahu' | 'Ketu';

type PlanetData = {
  name: PlanetName;
  house: number;           // 1-12
  rasi: number;            // 1-12 (zodiac sign)
  degree: number;          // 0-30 within sign
  nakshatra?: string;
  nakshatraPada?: number;
  retro: boolean;
  combust: boolean;
  exalted: boolean;
  debilitated: boolean;
  blendedPct?: number;     // 0-100
  longitude?: number;       // Full ecliptic longitude
};

type AspectData = {
  from: PlanetName;
  toHouse: number;
  kind: string;            // e.g., "7th aspect", "5th aspect"
};

type ChartData = {
  planets: PlanetData[];
  ascendant: {
    rasi: number;
    degree: number;
  };
  aspects?: AspectData[];
};

type Props = {
  chartData: ChartData;
  personName?: string;     // Person's name for header
  size?: number;           // Base size in pixels (default 500)
  backgroundImage?: string; // URL or path to background image
  showExport?: boolean;    // Show export button (default true)
  onBackgroundChange?: (bg: string) => void; // Callback for background changes
  onPlanetSelect?: (planet: PlanetName | null) => void; // Callback when planet is clicked
  selectedPlanet?: PlanetName | null; // Controlled selected planet (optional)
  onHouseSelect?: (houseNumber: number | null) => void; // Callback when house number is clicked
  onAnalyseChart?: () => void; // Callback for SWOT analysis button
  onGetRemedies?: () => void; // Callback for Get Remedies button
  onToggleDashboard?: () => void; // Callback for Dashboard toggle button (mobile)
  showDashboardView?: boolean; // Show dashboard view instead of chart (mobile)
  userRole?: 'admin' | 'member'; // User role for permission checks
  audioGuide?: React.ReactNode; // Optional audio guide control rendered inline with chart style icons
};

// ===================== CONSTANTS =====================

// Default planet colors (fallback)
const DEFAULT_PLANET_COLORS: Record<PlanetName, string> = {
  Sun: '#FF6B35',
  Moon: '#4A90E2',
  Mercury: '#50C878',
  Venus: '#E74C3C',
  Mars: '#C0392B',
  Jupiter: '#F39C12',
  Saturn: '#34495E',
  Rahu: '#8B4789',
  Ketu: '#7F8C8D'
};

// Functional benefic/malefic colors
const BENEFIC_COLOR = '#16a34a'; // Green
const MALEFIC_COLOR = '#dc2626'; // Red
const NEUTRAL_COLOR = '#d4b106'; // Yellow (matching App.tsx)

/**
 * Get planet color based on functional benefic/malefic nature
 * Uses the same source of truth as the main charts
 */
function getPlanetColor(planet: PlanetName, lagnaRashi: number): string {
  const funcMap = classifyFunctionalForLagna(lagnaRashi) as Record<string, 'benefic' | 'malefic' | 'neutral'>;
  const nature = funcMap[planet] ?? 'neutral';
  
  if (nature === 'benefic') return BENEFIC_COLOR;
  if (nature === 'malefic') return MALEFIC_COLOR;
  return NEUTRAL_COLOR;
}

// North Indian chart house positions - CORRECT COUNTER-CLOCKWISE STRUCTURE
// Houses go COUNTER-CLOCKWISE from top, matching traditional North Indian chart
// Positions adjusted with padding to prevent merging with chart borders
const HOUSE_POSITIONS: Record<number, { x: number; y: number; anchor: string }> = {
  1:  { x: 50,  y: 20,  anchor: 'top' },       // 1: Top center (ascendant)
  2:  { x: 27,  y: 13,  anchor: 'top-left' },  // 2: Top-left upper
  3:  { x: 12,  y: 26,  anchor: 'left' },      // 3: Top-left lower  
  4:  { x: 25,  y: 47,  anchor: 'left' },      // 4: Left center
  5:  { x: 12,  y: 74,  anchor: 'left' },      // 5: Bottom-left upper
  6:  { x: 27,  y: 87,  anchor: 'bottom-left' }, // 6: Bottom-left lower
  7:  { x: 50,  y: 80,  anchor: 'bottom' },    // 7: Bottom center (descendant)
  8:  { x: 73,  y: 87,  anchor: 'bottom-right' }, // 8: Bottom-right upper
  9:  { x: 88,  y: 74,  anchor: 'right' },     // 9: Bottom-right lower
  10: { x: 75,  y: 47,  anchor: 'right' },     // 10: Right center
  11: { x: 88,  y: 26,  anchor: 'right' },     // 11: Top-right lower
  12: { x: 73,  y: 13,  anchor: 'top-right' }  // 12: Top-right upper
};

// ===================== HELPER FUNCTIONS =====================
function norm360(x: number): number {
  return ((x % 360) + 360) % 360;
}

function getRashiNumber(house: number, ascRashi: number): number {
  // Whole-sign system: ascendant sign = house 1
  return ((ascRashi - 1 + (house - 1)) % 12) + 1;
}

function formatDegree(deg: number): string {
  const d = Math.floor(deg);
  return `${d}°`;
}

/**
 * Sort planets by degree for display ordering
 */
function sortPlanetsByDegree(planets: PlanetData[]): PlanetData[] {
  return [...planets].sort((a, b) => a.degree - b.degree);
}

// ===================== MAIN COMPONENT =====================
export default function AdvancedNorthIndianChart({
  chartData,
  personName,
  size = 400,
  backgroundImage,
  showExport = true,
  onBackgroundChange,
  onPlanetSelect,
  selectedPlanet: externalSelectedPlanet,
  onHouseSelect,
  onAnalyseChart,
  onGetRemedies,
  onToggleDashboard,
  showDashboardView = false,
  userRole,
  audioGuide,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // Stable ID used to scope data-aspects-toggle/item in HTML exports (no React JS in exported file)
  const chartId = useRef(`anic-${Math.random().toString(36).slice(2, 9)}`);
  
  const [hoveredPlanet, setHoveredPlanet] = useState<PlanetName | null>(null);
  const [internalSelectedPlanet, setInternalSelectedPlanet] = useState<PlanetName | null>(null);
  const [currentBackground, setCurrentBackground] = useState(backgroundImage || '/chart-backgrounds/north-indian-classic.svg');
  const [showAspectingPlanets, setShowAspectingPlanets] = useState(false);

  // Use external selected planet if provided (controlled), otherwise internal (uncontrolled)
  const selectedPlanet = externalSelectedPlanet !== undefined ? externalSelectedPlanet : internalSelectedPlanet;

  // Group planets by house for display
  const planetsByHouse = useCallback(() => {
    const grouped: Record<number, PlanetData[]> = {};
    for (let i = 1; i <= 12; i++) grouped[i] = [];
    
    chartData.planets.forEach(p => {
      if (p.house >= 1 && p.house <= 12) {
        grouped[p.house].push(p);
      }
    });
    
    return grouped;
  }, [chartData.planets]);

  const housePlanets = planetsByHouse();

  // Get aspecting planets for each house
  const aspectingPlanetsByHouse = useCallback((): Record<number, PlanetName[]> => {
    const byHouse: Record<number, Set<PlanetName>> = {};
    for (let i = 1; i <= 12; i++) byHouse[i] = new Set();
    
    if (!chartData.aspects) return {};
    
    chartData.aspects.forEach(aspect => {
      if (aspect.toHouse >= 1 && aspect.toHouse <= 12) {
        byHouse[aspect.toHouse].add(aspect.from);
      }
    });
    
    // Convert sets to sorted arrays
    const result: Record<number, PlanetName[]> = {};
    for (let i = 1; i <= 12; i++) {
      result[i] = Array.from(byHouse[i]).sort();
    }
    return result;
  }, [chartData.aspects]);

  const aspectingPlanets = aspectingPlanetsByHouse();
  
  // Debug aspecting planets
  useEffect(() => {
    console.log('=== ASPECTING PLANETS BY HOUSE ===');
    console.log(aspectingPlanets);
    Object.entries(aspectingPlanets).forEach(([house, planets]) => {
      if (planets.length > 0) {
        console.log(`House ${house}: ${planets.join(', ')}`);
      }
    });
  }, [aspectingPlanets]);

  // Get aspects for a specific planet
  const getAspectsForPlanet = useCallback((planet: PlanetName): AspectData[] => {
    if (!chartData.aspects) {
      console.log('No aspects in chartData');
      return [];
    }
    const aspects = chartData.aspects.filter(a => a.from === planet);
    console.log(`Aspects for ${planet}:`, aspects);
    return aspects;
  }, [chartData.aspects]);

  // Log all aspects on mount for debugging
  useEffect(() => {
    if (chartData.aspects) {
      console.log('=== ADVANCED CHART ASPECT DEBUG ===');
      console.log('Total aspects received:', chartData.aspects.length);
      console.log('All aspects:', chartData.aspects);
      
      // Group by planet
      const byPlanet: Record<string, any[]> = {};
      chartData.aspects.forEach(asp => {
        if (!byPlanet[asp.from]) byPlanet[asp.from] = [];
        byPlanet[asp.from].push(asp);
      });
      console.log('Aspects grouped by planet:', byPlanet);
      
      // Check which houses are aspected
      const aspectedHouses = new Set(chartData.aspects.map(a => a.toHouse));
      console.log('Houses receiving aspects:', Array.from(aspectedHouses).sort((a,b) => a-b));
    } else {
      console.log('No aspects data available');
    }
  }, [chartData.aspects]);

  // Export chart as image
  const exportChart = useCallback(async (format: 'png' | 'jpeg' = 'png') => {
    if (!containerRef.current) return;

    try {
      // Dynamically import html2canvas
      const html2canvas = (await import('html2canvas')).default;
      
      const originalCanvas = await html2canvas(containerRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher resolution
        logging: false,
        useCORS: true
      });

      // Create a square canvas by cropping to the smaller dimension
      const size = Math.min(originalCanvas.width, originalCanvas.height);
      const squareCanvas = document.createElement('canvas');
      squareCanvas.width = size;
      squareCanvas.height = size;
      
      const ctx = squareCanvas.getContext('2d');
      if (!ctx) return;
      
      // Center crop the original canvas
      const offsetX = (originalCanvas.width - size) / 2;
      const offsetY = (originalCanvas.height - size) / 2;
      
      ctx.drawImage(
        originalCanvas,
        offsetX, offsetY, size, size,  // Source rectangle (cropped)
        0, 0, size, size                // Destination rectangle
      );

      // Convert to blob and download
      squareCanvas.toBlob((blob) => {
        if (!blob) return;
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `north-indian-chart-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, `image/${format}`, 0.95);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Make sure html2canvas is installed: npm install html2canvas');
    }
  }, []);

  // Handle planet click
  const handlePlanetClick = (planet: PlanetName) => {
    const newSelection = selectedPlanet === planet ? null : planet; // Toggle if same planet clicked
    
    if (onPlanetSelect) {
      // Controlled mode - notify parent
      onPlanetSelect(newSelection);
    } else {
      // Uncontrolled mode - manage internally (legacy behavior, won't show modal anymore)
      setInternalSelectedPlanet(newSelection);
    }
  };

  // Get planet details for display
  const getSelectedPlanetData = (): PlanetData | null => {
    if (!selectedPlanet) return null;
    return chartData.planets.find(p => p.name === selectedPlanet) || null;
  };

  // Handle background change
  const handleBackgroundChange = (bg: string) => {
    setCurrentBackground(bg);
    if (onBackgroundChange) {
      onBackgroundChange(bg);
    }
  };

  // Determine if we should show style controls (only if backgroundImage is not externally controlled)
  const showStyleControls = backgroundImage === undefined;

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      {/* Header with person name and show aspects toggle */}
      {personName ? (
        // Interactive Chart tab: Compact horizontal layout with name and controls
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
          {/* Row 1: Person name (smaller) */}
          <Typography level="title-md" sx={{ fontWeight: 600, fontSize: '1rem', textAlign: 'left' }}>
            {personName}'s Chart
          </Typography>
          {/* Row 2: Controls horizontally */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
            <Tooltip title="View Planet Drishti" placement="top">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showAspectingPlanets}
                  onChange={(e) => setShowAspectingPlanets(e.target.checked)}
                  data-aspects-toggle={chartId.current}
                  style={{ cursor: 'pointer' }}
                />
                <Typography level="body-sm" sx={{ fontWeight: 500, color: '#334155', fontSize: '0.68rem' }}>
                  Show Aspects
                </Typography>
              </label>
            </Tooltip>
            
            {/* Export and Style controls */}
            {showExport && (
              <Tooltip title="Export as PNG" placement="top">
                <IconButton size="sm" variant="outlined" onClick={() => exportChart('png')} sx={{ minHeight: 28, minWidth: 28 }}>
                  <DownloadIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
            
            {showStyleControls && (
              <>
                <Tooltip title="Classic Style" placement="top">
                  <IconButton 
                    size="sm" 
                    variant={currentBackground === '/chart-backgrounds/north-indian-classic.svg' ? 'solid' : 'outlined'}
                    onClick={() => handleBackgroundChange('/chart-backgrounds/north-indian-classic.svg')}
                    sx={{ minHeight: 28, minWidth: 28, fontSize: '0.85rem' }}
                  >
                    📜
                  </IconButton>
                </Tooltip>
                <Tooltip title="Modern Style" placement="top">
                  <IconButton 
                    size="sm" 
                    variant={currentBackground === '/chart-backgrounds/north-indian-modern.svg' ? 'solid' : 'outlined'}
                    onClick={() => handleBackgroundChange('/chart-backgrounds/north-indian-modern.svg')}
                    sx={{ minHeight: 28, minWidth: 28, fontSize: '0.85rem' }}
                  >
                    ✨
                  </IconButton>
                </Tooltip>
                <Tooltip title="Royal Style" placement="top">
                  <IconButton 
                    size="sm" 
                    variant={currentBackground === '/chart-backgrounds/north-indian-royal.svg' ? 'solid' : 'outlined'}
                    onClick={() => handleBackgroundChange('/chart-backgrounds/north-indian-royal.svg')}
                    sx={{ minHeight: 28, minWidth: 28, fontSize: '0.85rem' }}
                  >
                    👑
                  </IconButton>
                </Tooltip>
              </>
            )}
            {audioGuide && audioGuide}
          </Box>
        </Box>
      ) : (
        // Calculator tab: Show Aspects on left + Analyse My Chart button
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <Tooltip title="View Planet Drishti" placement="right">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showAspectingPlanets}
                onChange={(e) => setShowAspectingPlanets(e.target.checked)}
                data-aspects-toggle={chartId.current}
                style={{ cursor: 'pointer' }}
              />
              <Typography level="body-sm" sx={{ fontWeight: 500, color: '#334155', fontSize: 12 }}>
                Show Aspects
              </Typography>
            </label>
          </Tooltip>
          {onToggleDashboard && (
            <button
              onClick={onToggleDashboard}
              className="gradient-border-button"
              style={{
                flexShrink: 0
              }}
            >
              <div className="gradient-border-button-inner">
                <span style={{ fontSize: '14px', lineHeight: 1 }}>{showDashboardView ? '🔄' : '📊'}</span>
                <span>{showDashboardView ? 'Show Chart' : 'Dashboard'}</span>
              </div>
            </button>
          )}
          {onAnalyseChart && (
            <button
              onClick={onAnalyseChart}
              className="gradient-border-button"
              style={{
                flexShrink: 0
              }}
            >
              <div className="gradient-border-button-inner">
                <span style={{ fontSize: '14px', lineHeight: 1 }}>🔮</span>
                <span>Analyse Chart</span>
              </div>
            </button>
          )}
          {onGetRemedies && (
            <button
              onClick={onGetRemedies}
              className="gradient-border-button"
              style={{
                flexShrink: 0
              }}
            >
              <div className="gradient-border-button-inner">
                <span style={{ fontSize: '14px', lineHeight: 1 }}>🌿</span>
                <span>Get Remedies</span>
              </div>
            </button>
          )}
        </Box>
      )}
      
      {/* Chart and controls side by side */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Chart container */}
        <Card
          ref={containerRef}
          sx={{
            position: 'relative',
            width: size,
            height: size,
            flexShrink: 0,
            overflow: 'hidden',
            bgcolor: 'transparent',
            backgroundImage: `url(${backgroundImage || currentBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            border: '2px solid',
            borderColor: 'neutral.outlinedBorder',
            boxShadow: 'lg'
          }}
        >
        {/* SVG Overlay for chart elements */}
        <svg
          ref={svgRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Outer square/rectangle frame */}
          <rect
            x="5" y="5" width="90" height="90"
            fill="none"
            stroke="#8B4513"
            strokeWidth="0.3"
            opacity="0.6"
          />
          
          {/* Diamond outline (inner diamond) */}
          <path
            d="M 50 5 L 95 50 L 50 95 L 5 50 Z"
            fill="none"
            stroke="#8B4513"
            strokeWidth="0.4"
            opacity="0.7"
          />
          
          {/* Diagonal lines from corners of outer square to opposite corners (forming X) */}
          <line x1="5" y1="5" x2="95" y2="95" stroke="#8B4513" strokeWidth="0.3" opacity="0.6" />
          <line x1="95" y1="5" x2="5" y2="95" stroke="#8B4513" strokeWidth="0.3" opacity="0.6" />

          {/* Zodiac sign numbers - positioned at INNER edges matching North Indian chart structure */}
          {(() => {
            // North Indian chart: Houses go COUNTER-CLOCKWISE from top center
            // All houses positioned with symmetry - COMPLETE
            const zodiacDisplayPositions: Record<number, { x: number; y: number }> = {
              1:  { x: 50,  y: 42 },   // House 1: LOCKED - Top center
              2:  { x: 28,  y: 22 },   // House 2: LOCKED - Top-left triangle
              3:  { x: 22,  y: 27 },   // House 3: LOCKED - Left edge, upper area
              4:  { x: 42,  y: 50 },   // House 4: LOCKED - Left center
              5:  { x: 21,  y: 73 },   // House 5: LOCKED - Left edge, lower area
              6:  { x: 28,  y: 78 },   // House 6: LOCKED - Mirror of H2 (bottom-left)
              7:  { x: 50,  y: 58 },   // House 7: LOCKED - Bottom center
              8:  { x: 72,  y: 78 },   // House 8: Mirror of H12 (bottom-right)
              9:  { x: 79,  y: 73 },   // House 9: Mirror of H5 (right edge, lower)
              10: { x: 58,  y: 50 },   // House 10: LOCKED - Right center
              11: { x: 78,  y: 27 },   // House 11: LOCKED - Mirror of H3 (right edge, upper)
              12: { x: 72,  y: 22 }    // House 12: LOCKED - Mirror of H2 (top-right)
            };
            
            return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(house => {
              const rashi = getRashiNumber(house, chartData.ascendant.rasi);
              const pos = zodiacDisplayPositions[house];
              
              return (
                <text
                  key={`zodiac-${house}`}
                  x={pos.x}
                  y={pos.y}
                  fontSize="2.2"
                  fontWeight="700"
                  fill="#dc2626"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  opacity="1"
                  fontFamily="Arial, sans-serif"
                  style={{ 
                    textShadow: '0 0 3px white, 0 0 5px white',
                    cursor: onHouseSelect ? 'pointer' : 'default',
                    pointerEvents: onHouseSelect ? 'auto' : 'none'
                  }}
                  onClick={onHouseSelect ? () => onHouseSelect(house) : undefined}
                >
                  {rashi}
                </text>
              );
            });
          })()}

          {/* Aspect arrows (only when hovering) */}
          {hoveredPlanet && getAspectsForPlanet(hoveredPlanet).map((aspect, idx) => {
            const fromHouse = chartData.planets.find(p => p.name === aspect.from)?.house || 1;
            const fromPos = HOUSE_POSITIONS[fromHouse];
            const toPos = HOUSE_POSITIONS[aspect.toHouse];
            
            console.log(`Rendering aspect ${idx}: ${aspect.from} -> House ${aspect.toHouse} (${aspect.kind})`);
            
            // Calculate arrow path (curved) with slight offset for multiple aspects
            const baseOffsetFactor = 0.15;
            const offsetMultiplier = idx * 0.03; // Slight offset for multiple aspects
            const midX = (fromPos.x + toPos.x) / 2;
            const midY = (fromPos.y + toPos.y) / 2;
            const controlX = midX + (fromPos.y - toPos.y) * (baseOffsetFactor + offsetMultiplier);
            const controlY = midY - (fromPos.x - toPos.x) * (baseOffsetFactor + offsetMultiplier);

            const planetColor = getPlanetColor(aspect.from, chartData.ascendant.rasi);
            
            // Color coding by planet type (all Jupiter aspects are green/benefic)
            const aspectColor = aspect.from === 'Jupiter' ? '#16a34a' :
                               aspect.from === 'Saturn' ? '#dc2626' :
                               aspect.from === 'Mars' ? '#f97316' :
                               planetColor;
            
            return (
              <g key={`aspect-${aspect.from}-${aspect.toHouse}-${idx}`}>
                {/* Thin elegant aspect line */}
                <path
                  d={`M ${fromPos.x} ${fromPos.y} Q ${controlX} ${controlY} ${toPos.x} ${toPos.y}`}
                  fill="none"
                  stroke={aspectColor}
                  strokeWidth="0.5"
                  strokeDasharray="2,1.5"
                  opacity="0.75"
                  markerEnd={`url(#arrowhead-${idx})`}
                />
              </g>
            );
          })}

          {/* Arrow marker definitions - one per aspect instance */}
          <defs>
            {hoveredPlanet && getAspectsForPlanet(hoveredPlanet).map((aspect, idx) => {
              const aspectColor = aspect.from === 'Jupiter' ? '#16a34a' :
                                 aspect.from === 'Saturn' ? '#dc2626' :
                                 aspect.from === 'Mars' ? '#f97316' :
                                 getPlanetColor(aspect.from, chartData.ascendant.rasi);
              return (
                <marker
                  key={`marker-${idx}`}
                  id={`arrowhead-${idx}`}
                  markerWidth="8"
                  markerHeight="8"
                  refX="7"
                  refY="2.5"
                  orient="auto"
                >
                  <polygon points="0 0, 8 2.5, 0 5" fill={aspectColor} opacity="0.75" />
                </marker>
              );
            })}
          </defs>
        </svg>

        {/* Planets in houses */}
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(house => {
          const planets = sortPlanetsByDegree(housePlanets[house]);
          const hasAspectingPlanets = showAspectingPlanets && aspectingPlanets[house] && aspectingPlanets[house].length > 0;
          
          // Skip if no planets AND no aspecting planets to show
          if (planets.length === 0 && !hasAspectingPlanets) return null;

          const pos = HOUSE_POSITIONS[house];
          
          return (
            <Box
              key={`house-${house}`}
              sx={{
                position: 'absolute',
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
                pointerEvents: 'auto',
                maxWidth: '90px',
                overflow: 'visible'
              }}
            >
              {planets.map(planet => {
                const planetColor = getPlanetColor(planet.name, chartData.ascendant.rasi);
                return (
                <Box
                  key={planet.name}
                  onMouseEnter={() => setHoveredPlanet(planet.name)}
                  onMouseLeave={() => setHoveredPlanet(null)}
                  onClick={() => handlePlanetClick(planet.name)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.2,
                    whiteSpace: 'nowrap',
                    px: 0.5,
                    py: 0,
                    borderRadius: 'sm',
                    bgcolor: selectedPlanet === planet.name ? `${planetColor}20` : 'transparent',
                    border: selectedPlanet === planet.name ? `2px solid ${planetColor}` : '2px solid transparent',
                    '&:hover': {
                      transform: 'scale(1.15)',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                      zIndex: 10
                    }
                  }}
                >
                  {/* Planet name */}
                  <span style={{ 
                    fontSize: '0.71rem', 
                    fontWeight: 700,
                    color: planetColor,
                    textShadow: hoveredPlanet === planet.name 
                      ? `0 0 8px ${planetColor}40, 0 1px 2px rgba(255,255,255,0.9)` 
                      : '0 1px 2px rgba(255,255,255,0.9)',
                    letterSpacing: '0.01em',
                    lineHeight: 1
                  }}>
                    {planet.name.substring(0, 2)}
                  </span>
                  
                  {/* Degree with subtle styling */}
                  <span style={{ 
                    fontSize: '0.56rem', 
                    fontWeight: 500,
                    color: '#64748b',
                    textShadow: '0 1px 1px rgba(255,255,255,0.8)',
                    lineHeight: 1
                  }}>
                    {formatDegree(planet.degree)}
                  </span>
                  
                  {/* Status indicators - more prominent dignities */}
                  {(planet.exalted || planet.debilitated || planet.retro || planet.combust) && (
                    <span style={{ 
                      fontSize: '0.75rem',
                      color: planet.exalted ? '#16a34a' : 
                             planet.debilitated ? '#dc2626' : 
                             planet.retro ? '#f59e0b' : '#64748b',
                      fontWeight: 900,
                      marginLeft: '2px',
                      lineHeight: 1
                    }}>
                      {planet.exalted && '↑'}
                      {planet.debilitated && '↓'}
                      {planet.retro && 'ᴿ'}
                      {planet.combust && '☼'}
                    </span>
                  )}
                </Box>
              );})}
              
              {/* Aspecting planets — always in DOM so HTML export can toggle them via script */}
              {aspectingPlanets[house] && aspectingPlanets[house].length > 0 && (
                <Typography
                  data-aspects-item={chartId.current}
                  style={{ display: showAspectingPlanets ? '' : 'none' }}
                  sx={{
                    fontSize: '0.53rem',
                    fontWeight: 500,
                    color: '#64748b',
                    mt: 0.5,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    opacity: 0.8
                  }}
                >
                  {aspectingPlanets[house].map(p => p.substring(0, 2)).join(' ')}
                </Typography>
              )}
            </Box>
          );
        })}

        {/* Ascendant label (simple text) */}
        <Box
          sx={{
            position: 'absolute',
            left: `${HOUSE_POSITIONS[1].x}%`,
            top: `${HOUSE_POSITIONS[1].y - 6}%`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none'
          }}
        >
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              color: '#8B4513',
              opacity: 0.8,
              textAlign: 'center',
              lineHeight: 1
            }}
          >
            ASC {formatDegree(chartData.ascendant.degree)}
          </Typography>
        </Box>
        </Card>
      </Box>

      {/* Planet Details Modal - REMOVED, use onPlanetSelect callback instead */}
    </Box>
  );
}
// ===== END FILE: src/components/AdvancedNorthIndianChart.tsx =====
